import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import "./Shop.scss";

function slugify(value = "") {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

function getInventoryKey(product) {
  const firstVariant = product?.variants?.[0];
  return (
    firstVariant?.stripePriceId ||
    product?._discogs?.releaseId ||
    `${product?.title || "untitled"}::${firstVariant?.id || product?.id || "unknown"}`
  );
}

function mergeCatalogProducts(products = []) {
  const mergedByKey = new Map();

  for (const product of products) {
    const key = getInventoryKey(product);
    const existing = mergedByKey.get(key);
    const quantity = Math.max(
      1,
      Number.parseInt(product.inventoryQuantity || product.quantity || 1, 10) || 1
    );

    if (existing) {
      existing.inventoryQuantity += quantity;
      continue;
    }

    mergedByKey.set(key, {
      ...product,
      inventoryQuantity: quantity,
    });
  }

  const seenHandles = new Set();
  return Array.from(mergedByKey.values()).map((product) => {
    const baseHandle = product.handle || slugify(product.title || product.id || "product");
    let handle = baseHandle;

    if (seenHandles.has(handle)) {
      const suffix = product?._discogs?.releaseId || product.id;
      handle = `${baseHandle}-${suffix}`;
      let counter = 2;
      while (seenHandles.has(handle)) {
        handle = `${baseHandle}-${suffix}-${counter}`;
        counter += 1;
      }
    }

    seenHandles.add(handle);
    return handle === product.handle ? product : { ...product, handle };
  });
}

const Shop = ({ products, isPDP }) => {
  const { addToCart, openCart, clearCart } = useCart();
  const location = useLocation();
  const mergedProducts = useMemo(() => mergeCatalogProducts(products), [products]);
  const didHandleCheckoutReturnRef = useRef(false);
  const clearCartRef = useRef(clearCart);

  const [categories, setCategories] = useState(["All"]); // Start with "All"
  const [filteredProducts, setFilteredProducts] = useState(mergedProducts);
  const [activeFilter, setActiveFilter] = useState("All");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  useEffect(() => {
    if (!mergedProducts || mergedProducts.length === 0) {
      setCategories(["All"]);
      setFilteredProducts([]);
      return;
    }

    const uniqueTypes = Array.from(
      new Set(mergedProducts.map((p) => p.productType).filter(Boolean))
    );
    // Combine "All" with whatever unique productTypes exist
    setCategories(["All", ...uniqueTypes]);
    // Reset filteredProducts to show all by default
    setFilteredProducts(mergedProducts);
  }, [mergedProducts]);

  // Filtering logic
  const handleFilter = (category) => {
    setActiveFilter(category);

    if (category === "All") {
      setFilteredProducts(mergedProducts);
    } else {
      // Filter by exact match of productType
      const newFiltered = mergedProducts.filter((p) => p.productType === category);
      setFilteredProducts(newFiltered);
    }
  };

  // Open the Quick Buy Modal
  const handleQuickBuy = (product) => {
    // Set the product we want to buy
    setModalProduct(product);
    // Default variant selection to the first variant
    const defaultVariantId = product?.variants?.[0]?.id || null;
    setSelectedVariantId(defaultVariantId);
    setIsModalOpen(true);
  };

  // Close the Quick Buy Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setModalProduct(null);
  };

  // Add to Cart from the Quick Buy Modal
  const handleAddToCartModal = () => {
    if (!modalProduct || !selectedVariantId) return;

    const selectedVariant = modalProduct.variants.find(
      v => v.id === selectedVariantId
    );

    if (selectedVariant) {
      addToCart(modalProduct, selectedVariant, 1);
      closeModal();
      openCart();
    }
  };

  useEffect(() => {
    // If on PDP route, lock scroll; otherwise restore it
    document.body.style.overflow = isPDP ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isPDP]);

  useEffect(() => {
    clearCartRef.current = clearCart;
  }, [clearCart]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isSuccessReturn = params.get("success") === "true";

    if (!isSuccessReturn) {
      didHandleCheckoutReturnRef.current = false;
      return;
    }

    if (!didHandleCheckoutReturnRef.current) {
      didHandleCheckoutReturnRef.current = true;
      clearCartRef.current();
    }
  }, [location.search]);

  return (
    <div className="shop">
      <header className="shop-header">
        <h1>Spinnin' Beans Shop</h1>

        {/* Dynamic Navigation Menu */}
        <nav>
          <ul>
            {categories.map((cat) => (
              <li key={cat}>
                <a
                  href={`#${cat}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFilter(cat);
                  }}
                  className={activeFilter === cat ? "active" : ""}
                >
                  {cat}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="shop-main">
        <Routes>
          <Route
            path="/"
            element={
              <section className="product-grid">
                {filteredProducts.map((product) => {
                  const productImage = product.images?.[0]?.src || 'fallback.jpg';
                  const productPrice = product.variants?.[0]?.price?.amount || '0.00';

                  // Use product.handle for the URL
                  return (
                    <article key={product.id} className="product-card">
                      <Link to={`product/${product.handle}`} className="product-link">
                        <img
                          src={productImage}
                          alt={product.title}
                          onError={(e) => { e.target.src = '/assets/sblogo.png'; }}
                        />
                        <h3>{product.title}</h3>
                        <p className="price">${productPrice}</p>
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleQuickBuy(product)}
                      >
                        Quick Buy
                      </button>
                    </article>
                  );
                })}
              </section>
            }
          />
          {/* Use :handle instead of :id here */}
          <Route path="product/:handle" element={<ProductDetail products={mergedProducts} />} />
        </Routes>
      </main>
      {/* Quick Buy Modal */}
      {isModalOpen && modalProduct && (
        <div className="quick-buy-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal}>×</button>

            <h2>{modalProduct.title}</h2>
            <p>${(modalProduct.variants?.[0]?.price?.amount || "0.00")}</p>
            <div className="modal-images">
              {modalProduct.images?.map((img, idx) => (
                <img 
                  key={idx} 
                  src={img.src} 
                  alt={`${modalProduct.title} - View ${idx + 1}`} 
                  onError={(e) => { e.target.src = '/assets/sblogo.png'; }}
                />
              ))}
            </div>

            {/* Variant Selection */}
            {modalProduct.variants && modalProduct.variants.length > 1 && (
              <div className="variant-selector">
                <label htmlFor="variant-select">Select Size:</label>
                <select
                  id="variant-select"
                  value={selectedVariantId || ""}
                  onChange={(e) => setSelectedVariantId(e.target.value)}
                >
                  {modalProduct.variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Add to Cart from Modal */}
            <button className="add-to-cart" onClick={handleAddToCartModal}>
              Add to Cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductDetail = ({ products }) => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();

  // Refs for the two panels
  const imagesRef = useRef(null);
  const contentRef = useRef(null);

  // Touch tracking ref
  const lastTouchYRef = useRef(null);

  // Find the product by handle
  const product = products.find((p) => p.handle === handle);
  const defaultVariantId = product?.variants?.[0]?.id || null;
  const [selectedVariantId, setSelectedVariantId] = useState(
    defaultVariantId
  );
  const [addedFeedback, setAddedFeedback] = useState(null);

  useEffect(() => {
    setSelectedVariantId(defaultVariantId);
  }, [defaultVariantId]);

  useEffect(() => {
    // ====================
    //   WHEEL SCROLL
    // ====================
    function handleWheel(e) {
      e.preventDefault(); // We will manually handle scroll

      const imagesEl = imagesRef.current;
      const infoEl = contentRef.current;
      if (!imagesEl || !infoEl) return;

      const deltaY = e.deltaY;
      const isScrollingDown = deltaY > 0;

      // Calculate how far each panel can scroll
      const imagesScrollTop = imagesEl.scrollTop;
      const imagesMaxScroll = imagesEl.scrollHeight - imagesEl.clientHeight;

      const infoScrollTop = infoEl.scrollTop;
      const infoMaxScroll = infoEl.scrollHeight - infoEl.clientHeight;

      // Check if the images panel is actually scrollable
      const canScrollImages = imagesMaxScroll > 0;
      // Check if the info panel is actually scrollable
      const canScrollInfo = infoMaxScroll > 0;

      if (isScrollingDown) {
        // SCROLLING DOWN
        if (canScrollImages && imagesScrollTop < imagesMaxScroll) {
          // Images not yet at bottom => keep scrolling images
          imagesEl.scrollTop = Math.min(
            imagesScrollTop + deltaY,
            imagesMaxScroll
          );
        } else {
          // Otherwise, scroll the info panel
          if (canScrollInfo) {
            infoEl.scrollTop = Math.min(
              infoScrollTop + deltaY,
              infoMaxScroll
            );
          }
        }
      } else {
        // SCROLLING UP (deltaY < 0)
        if (canScrollInfo && infoScrollTop > 0) {
          // Info not at top => scroll info upward
          infoEl.scrollTop = Math.max(infoScrollTop + deltaY, 0);
        } else {
          // Otherwise, scroll images upward
          if (canScrollImages) {
            imagesEl.scrollTop = Math.max(imagesScrollTop + deltaY, 0);
          }
        }
      }
    }

    // ====================
    //  TOUCH SCROLL
    // ====================
    function handleTouchStart(e) {
      if (e.touches && e.touches.length > 0) {
        lastTouchYRef.current = e.touches[0].pageY;
      }
    }

    function handleTouchMove(e) {
      e.preventDefault(); // We'll do manual scroll

      const imagesEl = imagesRef.current;
      const infoEl = contentRef.current;
      if (!imagesEl || !infoEl) return;

      const currentY = e.touches[0].pageY;
      const prevY = lastTouchYRef.current ?? currentY;
      const deltaY = prevY - currentY; // Positive if swiping up, negative if swiping down
      lastTouchYRef.current = currentY;

      // Reuse the same logic as wheel
      handleWheel({ preventDefault: () => { }, deltaY });
    }

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  if (!product) return <div>Product not found</div>;

  const productImages = product.images || [];
  const productPrice = product.variants?.[0]?.price?.amount || "0.00";

  // "Add to Cart" logic
  const handleAddToCart = () => {
    if (!product || !selectedVariantId) return;

    const selectedVariant = product.variants.find(
      v => v.id === selectedVariantId
    );

    if (selectedVariant) {
      addToCart(product, selectedVariant, 1);
      setAddedFeedback(`1x ${selectedVariant.title} added to cart`);
      setTimeout(() => setAddedFeedback(null), 2500);
      openCart();
    }
  };

  return createPortal(
    <div className="product-detail">
      <button className="close-button" onClick={() => navigate("/shop")}>
        ×
      </button>
      <div className="pdp-container">
        {/* Left side: images */}
        <div className="pdp-images" ref={imagesRef}>
          {productImages.map((img, index) => (
            <img
              key={index}
              src={img.src}
              alt={`${product.title} - View ${index + 1}`}
              onError={(e) => { e.target.src = '/assets/sblogo.png'; }}
            />
          ))}
        </div>
        {/* Right side: info */}
        <div className="product-info" ref={contentRef}>
          <h2>{product.title}</h2>

          {/* Genre & Style Tags - Move to top */}
          {(product.genres?.length > 0 || product.styles?.length > 0) && (
            <div className="genre-tags">
              {product.genres?.map((genre, idx) => (
                <span key={`genre-${idx}`} className="tag genre-tag">{genre}</span>
              ))}
              {product.styles?.map((style, idx) => (
                <span key={`style-${idx}`} className="tag style-tag">{style}</span>
              ))}
            </div>
          )}

          <p className="price">${parseFloat(productPrice).toFixed(2)}</p>

          {product.variants && product.variants.length > 1 && (
            <div className="variant-selector">
              <label htmlFor="variant-select">Select Size:</label>
              <select
                id="variant-select"
                value={selectedVariantId}
                onChange={(e) => setSelectedVariantId(e.target.value)}
              >
                {product.variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button className="add-to-cart" onClick={handleAddToCart}>
            Add to Cart
          </button>
          {addedFeedback && (
            <p className="atc-feedback">{addedFeedback}</p>
          )}

          {product.descriptionHtml && (
            <div className="product-description">
              <h3>Description</h3>
              <div dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
            </div>
          )}

          {/* Side-by-side wrapper: unified chart on left, tracklist on right (vinyl only) */}
          {product.productType !== 'Apparel' && (
            <div className="details-wrapper">
              {/* Left: Unified Release Info Chart */}
              <div className="release-info">
                {product.descriptionHtml && extractArtist(product.descriptionHtml) && (
                  <div className="info-row">
                    <strong>Artist:</strong>
                    <span>{extractArtist(product.descriptionHtml)}</span>
                  </div>
                )}

                {product.descriptionHtml && extractLabel(product.descriptionHtml) && (
                  <div className="info-row">
                    <strong>Label:</strong>
                    <span>{extractLabel(product.descriptionHtml)}</span>
                  </div>
                )}

                {product.descriptionHtml && extractYear(product.descriptionHtml) && (
                  <div className="info-row">
                    <strong>Year:</strong>
                    <span>{extractYear(product.descriptionHtml)}</span>
                  </div>
                )}

                {product.catalogNumber && (
                  <div className="info-row">
                    <strong>Catalog #:</strong>
                    <span>{product.catalogNumber}</span>
                  </div>
                )}

                {product.country && (
                  <div className="info-row">
                    <strong>Country:</strong>
                    <span>{product.country}</span>
                  </div>
                )}

                {product.formatDetails && product.formatDetails.length > 0 && (
                  <div className="info-row">
                    <strong>Format:</strong>
                    <span>{product.formatDetails.join(', ')}</span>
                  </div>
                )}

              </div>

              {/* Right: Tracklist */}
              {product.tracklist && product.tracklist.length > 0 && (
                <div className="tracklist-section">
                  <h3>Tracklist</h3>
                  {renderTracklist(product.tracklist)}
                </div>
              )}
            </div>
          )}

          <footer className="product-footer">
            <p>© 2026 Spinnin' Beans. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Helper functions to extract data from descriptionHtml
function extractLabel(html) {
  const match = html.match(/Label:\s*([^<]+)/);
  return match ? match[1] : '';
}

function extractArtist(html) {
  const match = html.match(/Artist:\s*([^<]+)/);
  return match ? match[1] : '';
}

function extractYear(html) {
  const match = html.match(/Year:\s*([^<]+)/);
  return match ? match[1] : '';
}

// Helper function to render tracklist grouped by vinyl side
function renderTracklist(tracks) {
  // Group tracks by side (A, B, C, D, etc.)
  const sides = {};

  tracks.forEach(track => {
    if (track.type_ === 'track' && track.position) {
      const side = track.position.charAt(0); // Get first character (A, B, etc.)
      if (!sides[side]) {
        sides[side] = [];
      }
      sides[side].push(track);
    }
  });

  return (
    <div className="tracklist-container">
      {Object.keys(sides).sort().map(side => (
        <div key={side} className="vinyl-side">
          <div className="side-header">
            <span className="side-label">Side {side}</span>
          </div>
          <div className="tracks">
            {sides[side].map((track, idx) => (
              <div key={idx} className="track">
                <span className="track-position">{track.position}</span>
                <div className="track-info">
                  <span className="track-title">{track.title}</span>
                  {track.extraartists && track.extraartists.length > 0 && (
                    <span className="track-artists">
                      ({track.extraartists.map(a => a.name).join(', ')})
                    </span>
                  )}
                </div>
                {track.duration && <span className="track-duration">{track.duration}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default Shop;
